import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  FlexProps,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { FormattedMessage } from "react-intl";

import { DISABLED_OPACITY, LayersIcon } from "@nens/ranalib";
import { capitalizeFirst } from "@nens/shared-utils";

import { RanaMap } from "@/lib/rana-map";

// VisibleLayer contains the file descrtions
export interface VisibleLayer {
  descriptorId: string;
  layer: string;
}

interface LayerMenuItemProps extends FlexProps {
  title: string;
  isVisible: boolean;
  setVisible: (bool: boolean) => void;
  onClick?: () => void;
  isSelected?: boolean;
  disabledText?: string;
}

interface LayerControlProps {
  descriptorId: string;
  title: string;
  layers: string[];
  visibleLayers: VisibleLayer[];
  setVisibleLayers: React.Dispatch<React.SetStateAction<VisibleLayer[]>>;
  onClick?: () => void;
  isSelected?: boolean;
  onSubLayerClick?: (layer: string) => void;
  isSubLayerSelected?: (layer: string) => boolean;
  // Layer: reason of disabling
  disabledLayers?: { [layer: string]: string };
  fullscreen?: boolean;
}

export function LayerControlHeader() {
  return (
    <RanaMap.ControlsHeader>
      <LayersIcon color="brand.sky" />
      <FormattedMessage id="filepreview.layers" defaultMessage="Layers" />
    </RanaMap.ControlsHeader>
  );
}

export function LayerMenuItem({
  title,
  isVisible,
  setVisible,
  onClick,
  isSelected,
  disabledText,
  ...rest
}: LayerMenuItemProps) {
  const isLayerMenuDisabled = !!disabledText;
  const isButton = !!onClick;

  return (
    <Tooltip
      label={disabledText}
      isDisabled={!isLayerMenuDisabled}
      hasArrow
      placement="right"
      ml={-3}
      fontSize="md"
      openDelay={200}
      closeOnClick={false}
    >
      <Flex
        opacity={isLayerMenuDisabled ? DISABLED_OPACITY : undefined}
        cursor={isLayerMenuDisabled ? "not-allowed" : undefined}
        w="full"
        role="group"
        fontSize={16}
        px={2}
        alignItems="center"
        overflow="auto"
        onClick={onClick}
        {...rest}
      >
        <Box
          as={isButton ? "button" : undefined}
          py={2}
          w="full"
          textAlign="left"
          title={capitalizeFirst(title)}
          whiteSpace="nowrap"
          textOverflow="ellipsis"
          overflow="hidden"
        >
          {capitalizeFirst(title)}
        </Box>

        <IconButton
          variant="fourthiary"
          aria-label="eye"
          icon={isVisible ? <FaRegEye /> : <FaRegEyeSlash />}
          visibility={isVisible ? "hidden" : "visible"}
          _groupHover={{
            visibility: "visible",
          }}
          pointerEvents={isLayerMenuDisabled ? "none" : undefined}
          onClick={(e) => {
            e.stopPropagation();
            setVisible(!isVisible);
          }}
        />
      </Flex>
    </Tooltip>
  );
}

export function LayerControl({
  descriptorId,
  title,
  layers,
  visibleLayers,
  setVisibleLayers,
  onClick,
  isSelected,
  onSubLayerClick,
  isSubLayerSelected,
  disabledLayers,
  fullscreen,
}: LayerControlProps) {
  // Filter visible layers for this file using the file descriptor id
  const fileVisibleLayers = visibleLayers.filter((layer) => layer.descriptorId === descriptorId);
  const isButton = !!onClick || layers.length > 1;
  let hoverBg = isButton ? "semantic.info.background" : "brand.bg";
  if (isSelected) hoverBg = "brand.selected";

  return (
    <Accordion allowToggle defaultIndex={fullscreen ? undefined : [0]}>
      <AccordionItem maxW="sm" borderBottomWidth={0} borderColor="brand.grey.300">
        <AccordionButton
          bg={isSelected ? "brand.selected" : undefined}
          _hover={{ bg: hoverBg }}
          as="div"
          cursor="pointer"
          p={0}
          pl={2}
        >
          {layers.length > 1 && <AccordionIcon />}
          <LayerMenuItem
            title={title}
            onClick={onClick}
            isSelected={isSelected}
            isVisible={fileVisibleLayers.length > 0}
            setVisible={() =>
              setVisibleLayers((currentlyVisibleLayers) =>
                fileVisibleLayers.length
                  ? currentlyVisibleLayers.filter((layer) => layer.descriptorId !== descriptorId)
                  : currentlyVisibleLayers.concat(
                      layers
                        .filter((layer) => !disabledLayers || !disabledLayers[layer])
                        .map((layer) => ({ descriptorId, layer }))
                    )
              )
            }
          />
        </AccordionButton>
        {layers.length > 1 && (
          <AccordionPanel p={0} ml={8} maxH={fullscreen ? undefined : 44} overflow="auto">
            {layers.map((layer) => {
              const disabledText = disabledLayers ? disabledLayers[layer] : undefined;
              return (
                <LayerMenuItem
                  key={layer}
                  title={layer}
                  fontSize={14}
                  disabledText={disabledText}
                  isVisible={fileVisibleLayers.some(
                    (l) => l.descriptorId === descriptorId && l.layer === layer
                  )}
                  setVisible={() =>
                    setVisibleLayers((allLayers) => {
                      const isVisible = allLayers.some(
                        (l) => l.descriptorId === descriptorId && l.layer === layer
                      );
                      return isVisible
                        ? allLayers.filter(
                            (l) => !(l.descriptorId === descriptorId && l.layer === layer)
                          )
                        : allLayers.concat({ descriptorId, layer });
                    })
                  }
                  onClick={onSubLayerClick ? () => onSubLayerClick(layer) : undefined}
                  isSelected={isSubLayerSelected && isSubLayerSelected(layer)}
                />
              );
            })}
          </AccordionPanel>
        )}
      </AccordionItem>
    </Accordion>
  );
}
